// App.js — Lapistics SwimTrack entry point
// Thin shell: imports, lazy page declarations, ProtectedLayout, RoleGuard, routes.
//
// Extracted modules (A1 modularisation, Session 17):
//   src/theme.js          — MUI createTheme config
//   src/pages/DemoGate.jsx — DemoShell + DemoEntry form

import React, { Suspense, lazy, useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Snackbar, Alert, Box, Button, Typography } from '@mui/material';
import { Timer as TimerIcon } from '@mui/icons-material';

import theme from './theme';
import Navbar from './components/Navbar';
import SwimLoader from './components/SwimLoader';
import WakingUpOverlay from './components/WakingUpOverlay';
import { useDemoTracking } from './hooks/useDemoTracking';
import ErrorBoundary from './components/ErrorBoundary';
import DemoModeBanner from './components/DemoModeBanner';
import { AuthProvider, useAuth } from './context/AuthContext';

// ─────────────────────────────────────────
// LAZY PAGE IMPORTS
// ─────────────────────────────────────────
const lazyWithPreload = (importFn) => {
  const Component = lazy(importFn);
  Component.preload = importFn;
  return Component;
};

const Login        = lazyWithPreload(() => import('./pages/Login'));
const Register     = lazyWithPreload(() => import('./pages/Register'));
const Dashboard    = lazyWithPreload(() => import('./pages/Dashboard'));
const Members      = lazyWithPreload(() => import('./pages/Members'));
const Events       = lazyWithPreload(() => import('./pages/Events'));
const RaceDay      = lazyWithPreload(() => import('./pages/RaceDay'));
const MyProfile    = lazyWithPreload(() => import('./pages/MyProfile'));
const MyChildren   = lazyWithPreload(() => import('./pages/MyChildren'));
const EnterEvents  = lazyWithPreload(() => import('./pages/EnterEvents'));
const Attendance   = lazyWithPreload(() => import('./pages/Attendance'));
const SeasonAwards = lazyWithPreload(() => import('./pages/SeasonAwards'));
const ClubRecords  = lazyWithPreload(() => import('./pages/ClubRecords'));
const ClubSettings = lazyWithPreload(() => import('./pages/ClubSettings'));
const Results      = lazyWithPreload(() => import('./pages/Results'));
const PlatformAdmin             = lazyWithPreload(() => import('./pages/PlatformAdmin'));
const PlatformAdminClubSelector = lazyWithPreload(() => import('./pages/PlatformAdminClubSelector'));
const ForgotPassword = lazyWithPreload(() => import('./pages/ForgotPassword'));
const ResetPassword  = lazyWithPreload(() => import('./pages/ResetPassword'));
const PrivacyPolicy  = lazyWithPreload(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyWithPreload(() => import('./pages/TermsOfService'));
const Impersonate    = lazyWithPreload(() => import('./pages/Impersonate'));
const InviteRegister = lazyWithPreload(() => import('./pages/InviteRegister'));
const Landing        = lazyWithPreload(() => import('./pages/Landing'));
const GetStarted     = lazyWithPreload(() => import('./pages/GetStarted'));
const EventLive      = lazyWithPreload(() => import('./pages/EventLive'));
const NotFound       = lazyWithPreload(() => import('./pages/NotFound'));
const DemoEntry      = lazyWithPreload(() => import('./pages/DemoGate'));
const PlatformEnter  = lazyWithPreload(() => import('./pages/PlatformEnter'));
const AuditLog       = lazyWithPreload(() => import('./pages/AuditLog'));
const HelpCenter     = lazyWithPreload(() => import('./pages/HelpCenter'));

// ─────────────────────────────────────────
// SUSPENSE WRAPPER — delays fallback to avoid flash
// ─────────────────────────────────────────
const DelayedSuspense = ({ children, fallback, delay = 200 }) => {
  const [showFallback, setShowFallback] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setShowFallback(true), delay);
    return () => { window.clearTimeout(timer); setShowFallback(false); };
  }, [delay]);
  return <Suspense fallback={showFallback ? fallback : null}>{children}</Suspense>;
};

// ─────────────────────────────────────────
// TK HEAT WATCHER — global background poll for timekeepers
// Polls every 5s regardless of which page the TK is on.
// When a heat goes ACTIVE and this TK has a lane assignment,
// shows a persistent banner with a "Go to Race Day" button.
// Dismisses automatically once they navigate to /race-day.
// ─────────────────────────────────────────
const TKHeatWatcher = () => {
  const { user, clubId } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [banner, setBanner]     = useState(null); // { heatId, category }
  const [countdown, setCountdown] = useState(null); // seconds until auto-nav
  const seenHeatRef   = useRef(null);
  const pollRef       = useRef(null);
  const countdownRef  = useRef(null);

  const userRoles    = (user?.role || '').split(',').map(r => r.trim());
  const isTK         = userRoles.includes('timekeeper') || userRoles.includes('admin');
  const userMemberId = user?.member_profile?.member_id ?? null;
  const onRaceDay    = location.pathname === '/race-day';

  // Clear banner + countdown when TK navigates to Race Day
  useEffect(() => {
    if (onRaceDay) {
      setBanner(null);
      setCountdown(null);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }
  }, [onRaceDay]);

  // Start a 10-second auto-navigate countdown when banner appears
  useEffect(() => {
    if (!banner) return;
    setCountdown(10);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          // Auto-navigate to Race Day
          setBanner(null);
          navigate('/race-day');
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [banner, navigate]);

  const checkForActiveHeat = useCallback(async () => {
    if (!isTK || !clubId || onRaceDay) return;
    if (document.visibilityState !== 'visible') return;
    try {
      const { getEvents, getEventHeats, getEventLaneAssignments } = await import('./api/api');
      const events = await getEvents(clubId);
      const today  = new Date().toDateString();
      const todayEvents = events.filter(
        e => !e.is_cancelled && new Date(e.date + 'T00:00:00').toDateString() === today
      );
      for (const ev of todayEvents) {
        const [heats, lanes] = await Promise.all([
          getEventHeats(ev.id),
          getEventLaneAssignments(ev.id),
        ]);
        const active = heats.find(h => h.status === 'ACTIVE');
        if (!active) continue;
        // Check if this TK has a lane in this heat
        const myLane = lanes.find(a =>
          (a.timekeeper_id && a.timekeeper_id === user?.id) ||
          (a.timekeeper_member_id && userMemberId && a.timekeeper_member_id === userMemberId)
        );
        if (!myLane) continue;
        const heatId = active.id;  // heat objects use 'id', not 'heat_id'
        if (seenHeatRef.current === heatId) return; // already notified for this heat
        seenHeatRef.current = heatId;
        setBanner({ heatId, category: active.category_name || active.category || '' });
        return;
      }
    } catch { /* silent */ }
  }, [isTK, clubId, onRaceDay, user?.id, userMemberId]);

  useEffect(() => {
    if (!isTK) return;
    pollRef.current = setInterval(checkForActiveHeat, 5000);
    checkForActiveHeat();
    return () => clearInterval(pollRef.current);
  }, [isTK, checkForActiveHeat]);

  if (!banner || onRaceDay) return null;
  return (
    <Box sx={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, bgcolor: '#CC0000', color: 'white',
      px: 3, py: 2, borderRadius: 2, boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', gap: 2,
      minWidth: 340, maxWidth: '90vw',
      animation: 'tkPulse 0.9s ease-in-out infinite',
      '@keyframes tkPulse': {
        '0%,100%': { boxShadow: '0 0 0 0 rgba(204,0,0,0.6)' },
        '50%':     { boxShadow: '0 0 0 16px rgba(204,0,0,0)' },
      },
    }}>
      <TimerIcon sx={{ fontSize: 32, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={800} fontSize="1rem" lineHeight={1.2}>
          🏊 Your heat is ACTIVE!
        </Typography>
        <Typography fontSize="0.82rem" sx={{ opacity: 0.9, mt: 0.3 }}>
          {banner.category ? `${banner.category} — ` : ''}Auto-opening in {countdown}s
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="small"
        onClick={() => {
          clearInterval(countdownRef.current);
          setBanner(null);
          navigate('/race-day');
        }}
        sx={{ bgcolor: 'white', color: '#CC0000', fontWeight: 800, flexShrink: 0,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
      >
        Open Now
      </Button>
      <Button size="small"
        sx={{ color: 'rgba(255,255,255,0.6)', minWidth: 0, p: 0.5, flexShrink: 0 }}
        onClick={() => {
          clearInterval(countdownRef.current);
          setBanner(null);
          setCountdown(null);
        }}>✕</Button>
    </Box>
  );
};

// ─────────────────────────────────────────
// PROTECTED LAYOUT
// ─────────────────────────────────────────
const ProtectedLayout = () => {
  const { token, user, selectedClubId } = useAuth();
  const location = useLocation();

  // Double-check: only show demo banner when the actual demo account is logged in.
  const isDemo =
    localStorage.getItem('isDemo') === 'true' &&
    user?.email === 'demo@lapistics.com.au';

  const demoVisitor = (() => {
    try { return JSON.parse(localStorage.getItem('demoVisitor')); } catch { return null; }
  })();
  useDemoTracking(isDemo ? demoVisitor?.email : null);

  // Proactively clear stale flag for non-demo users
  if (localStorage.getItem('isDemo') === 'true' && user && user.email !== 'demo@lapistics.com.au') {
    localStorage.removeItem('isDemo');
  }

  if (!token) return <Navigate to="/login" replace />;
  if (location.pathname === '/') return <Navigate to="/dashboard" replace />;
  if (
    user?.is_platform_admin && !selectedClubId &&
    location.pathname !== '/platform/select-club' &&
    location.pathname !== '/platform-admin'
  ) return <Navigate to="/platform/select-club" replace />;

  const NAVBAR_HEIGHT = 70;
  const BANNER_HEIGHT = 40;

  return (
    <>
      {isDemo && <DemoModeBanner />}
      <Navbar />
      <TKHeatWatcher />
      <main style={{
        paddingTop: isDemo ? `${BANNER_HEIGHT + NAVBAR_HEIGHT}px` : `${NAVBAR_HEIGHT}px`,
        minHeight: '100vh',
      }}>
        <DelayedSuspense fallback={<SwimLoader />} delay={200}><Outlet /></DelayedSuspense>
      </main>
    </>
  );
};

// ─────────────────────────────────────────
// ROLE GUARD
// ─────────────────────────────────────────
const RoleGuard = ({ children, roles, platformAdmin }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (platformAdmin && !user.is_platform_admin) return <Navigate to="/" replace />;
  if (roles) {
    const userRoles = (user?.role || '').split(',').map(r => r.trim()).filter(Boolean);
    // Platform admins bypass role checks — they can access any club-scoped page
    if (!user.is_platform_admin && !userRoles.some(r => roles.includes(r)))
      return <Navigate to="/" replace />;
  }
  return children;
};

// ─────────────────────────────────────────
// APP
// ─────────────────────────────────────────
export default function App() {
  const [sessionExpiredToast, setSessionExpiredToast] = useState(false);

  useEffect(() => {
    const handle = (e) => {
      if (e.detail?.reason === 'expired') setSessionExpiredToast(true);
    };
    window.addEventListener('authLogout', handle);
    return () => window.removeEventListener('authLogout', handle);
  }, []);

  useEffect(() => {
    const preload = () => { Dashboard.preload?.(); Members.preload?.(); RaceDay.preload?.(); MyProfile.preload?.(); };
    if ('requestIdleCallback' in window) { const id = window.requestIdleCallback(preload); return () => window.cancelIdleCallback(id); }
    const t = window.setTimeout(preload, 0); return () => window.clearTimeout(t);
  }, []);

  // ── Keep-alive ping (Session 64) ─────────────────────────────────────────
  // Pings GET /ping/ every 4 minutes while the tab is visible.
  // Prevents Render free-tier sleeping (sleeps after 15 min inactivity).
  // Uses plain fetch — no auth header, no axios interceptor, errors silently
  // ignored. Only fires when document is visible to avoid wasting requests
  // on backgrounded tabs.
  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const PING_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes

    const ping = () => {
      // Always ping regardless of visibility — the GitHub Actions workflow
      // covers overnight keep-alive, but this frontend ping catches active
      // sessions where the user is in another tab. The only case we skip is
      // when the document is fully hidden (mobile browser backgrounded to OS).
      if (document.hidden) return;
      fetch(`${API_BASE}/ping/`, { method: 'GET', credentials: 'omit' }).catch(() => {});
    };

    // Ping on first mount so the server warms up as soon as the app loads,
    // then repeat on interval.
    ping();
    const id = setInterval(ping, PING_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Snackbar
          open={sessionExpiredToast}
          autoHideDuration={5000}
          onClose={() => setSessionExpiredToast(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="warning" onClose={() => setSessionExpiredToast(false)} sx={{ fontFamily: "'Outfit',sans-serif" }}>
            Your session has expired — please sign in again.
          </Alert>
        </Snackbar>
        {/* Mounted at the top level (not inside ProtectedLayout) so cold-start
            feedback is consistent on public pages too — most importantly
            Login, which is the single most likely place a visitor actually
            hits a cold start (their very first request of the session).
            Previously this only rendered post-login, so Login.jsx had its
            own separate, cruder text-based handling instead. */}
        <WakingUpOverlay />
        <ErrorBoundary>
          <BrowserRouter>
            <Routes>
              <Route path="/"                element={<Landing />} />
              <Route path="/login"           element={<Login />} />
              <Route path="/register"        element={<Register />} />
              <Route path="/get-started"     element={<GetStarted />} />
              <Route path="/demo"            element={<DelayedSuspense fallback={<SwimLoader />}><DemoEntry /></DelayedSuspense>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password"  element={<ResetPassword />} />
              <Route path="/privacy-policy"  element={<PrivacyPolicy />} />
              <Route path="/terms"           element={<TermsOfService />} />
              <Route path="/impersonate"     element={<Impersonate />} />
              <Route path="/invite/:token"   element={<InviteRegister />} />
              <Route path="/event-live/:eventId" element={<DelayedSuspense fallback={<SwimLoader />}><EventLive /></DelayedSuspense>} />
              <Route path="/platform-enter" element={<DelayedSuspense fallback={<SwimLoader />}><PlatformEnter /></DelayedSuspense>} />

              <Route element={<ProtectedLayout />}>
                <Route path="/dashboard"     element={<ErrorBoundary key="dashboard"><Dashboard /></ErrorBoundary>} />
                <Route path="/members"       element={<RoleGuard roles={['admin','coach']}><ErrorBoundary key="members"><Members /></ErrorBoundary></RoleGuard>} />
                <Route path="/events"        element={<ErrorBoundary key="events"><Events /></ErrorBoundary>} />
                <Route path="/race-day"      element={<ErrorBoundary key="race-day"><RaceDay /></ErrorBoundary>} />
                <Route path="/my-profile"    element={<ErrorBoundary key="my-profile"><MyProfile /></ErrorBoundary>} />
                <Route path="/my-children"   element={<RoleGuard roles={['parent']}><ErrorBoundary key="my-children"><MyChildren /></ErrorBoundary></RoleGuard>} />
                <Route path="/results"       element={<ErrorBoundary key="results"><Results /></ErrorBoundary>} />
                <Route path="/platform/select-club" element={<RoleGuard platformAdmin><ErrorBoundary key="select-club"><PlatformAdminClubSelector /></ErrorBoundary></RoleGuard>} />
                <Route path="/enter-events"  element={<RoleGuard roles={['swimmer','parent']}><ErrorBoundary key="enter-events"><EnterEvents /></ErrorBoundary></RoleGuard>} />
                <Route path="/attendance"    element={<RoleGuard roles={['admin','attendance_marker','timekeeper']}><ErrorBoundary key="attendance"><Attendance /></ErrorBoundary></RoleGuard>} />
                <Route path="/season-awards" element={<RoleGuard roles={['admin','swimmer','viewer','parent','coach']}><ErrorBoundary key="season-awards"><SeasonAwards /></ErrorBoundary></RoleGuard>} />
                <Route path="/club-records"  element={<RoleGuard roles={['admin','swimmer','viewer','parent','coach']}><ErrorBoundary key="club-records"><ClubRecords /></ErrorBoundary></RoleGuard>} />
                <Route path="/club-settings" element={<RoleGuard roles={['admin']}><ErrorBoundary key="club-settings"><ClubSettings /></ErrorBoundary></RoleGuard>} />
                <Route path="/audit-log"    element={<RoleGuard roles={['admin']}><ErrorBoundary key="audit-log"><AuditLog /></ErrorBoundary></RoleGuard>} />
                {/* No RoleGuard — Help is useful to every role (admin, parent, swimmer, timekeeper). */}
                <Route path="/help"          element={<ErrorBoundary key="help"><HelpCenter /></ErrorBoundary>} />
                <Route path="/platform-admin" element={<RoleGuard platformAdmin><ErrorBoundary key="platform-admin"><PlatformAdmin /></ErrorBoundary></RoleGuard>} />
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Public catch-all — logged-out users hitting bad URLs */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </ThemeProvider>
    </AuthProvider>
  );
}
